import { ChangeEvent, useEffect, useRef, useState } from "react";
import JicFloatingWidget from "./jicFloatingWidget";
import LoveTransferPanel from "./loveTransferPanel";
import {
  describeFace,
  loadFaceRecognitionModels,
  readLocalFaceRegistry,
  resolveLocalFace,
} from "../lib/faceRecognition";
import { fetchFaceStats, fetchFaceStatus, resolveFaceOnServer, type FaceIdentity } from "../lib/faceRegistry";
import { fetchJicHandle, getJicToken, getJicUser, loginJic, onJicChange, type JicUser } from "../lib/jic";

type ScannerState = "idle" | "loading" | "scanning" | "matched" | "unknown" | "error";

// 统一服务端 Registry 与本机缓存两种来源的识别结果
type ResolvedFace = {
  handle: string;
  faceId: string;
  address?: string;
  verified: boolean;
  confidence: number;
  source: "registry" | "local";
  identities?: FaceIdentity[];
};

const IDENTITY_LABEL: Record<FaceIdentity["type"], string> = {
  jic: "JIC",
  address: "0x",
  x: "X",
  email: "MAIL",
  phone: "TEL",
};

function identityDisplay(identity: FaceIdentity) {
  if (identity.type === "address") return `${identity.value.slice(0, 6)}…${identity.value.slice(-4)}`;
  if (identity.type === "jic" || identity.type === "x") return `@${identity.value}`;
  return identity.value;
}

export default function FaceScanner() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const analyzingRef = useRef(false);
  const [state, setState] = useState<ScannerState>("idle");
  const [message, setMessage] = useState("点开相机，对准真人、屏幕或照片");
  const [match, setMatch] = useState<ResolvedFace | null>(null);
  const [registryCount, setRegistryCount] = useState<number | null>(null);
  const [jicUser, setJicUser] = useState<JicUser | null>(() => getJicUser());
  const [myName, setMyName] = useState<string | null>(null);
  const [myBound, setMyBound] = useState<boolean | null>(null);

  const stopScanner = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    loadFaceRecognitionModels().catch(() => undefined);
    fetchFaceStats().then((count) => {
      setRegistryCount(count ?? readLocalFaceRegistry().length);
    });
    return stopScanner;
  }, []);

  useEffect(() => onJicChange(setJicUser), []);

  // 登录后:查自己的短名字和绑定状态;没绑脸 → 自动带去绑定页(每次会话只跳一次)
  useEffect(() => {
    let cancelled = false;
    setMyName(null);
    setMyBound(null);
    if (!jicUser?.sub || !getJicToken()) return;
    (async () => {
      const name = await fetchJicHandle(jicUser.sub);
      if (cancelled) return;
      setMyName(name);
      if (!name) return;
      const status = await fetchFaceStatus(name);
      if (cancelled) return;
      const bound = Boolean(status?.bound);
      setMyBound(bound);
      if (!bound && sessionStorage.getItem("face3:autobind") !== "1") {
        sessionStorage.setItem("face3:autobind", "1");
        window.location.href = `/${name}`;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jicUser?.sub]);

  const loggedIn = Boolean(jicUser?.sub && getJicToken());

  const resolveInput = async (input: HTMLVideoElement | HTMLImageElement) => {
    if (analyzingRef.current) return;
    analyzingRef.current = true;
    try {
      const descriptor = await describeFace(input);
      if (!descriptor) {
        setMessage("没有找到清晰正脸，请调整角度或距离");
        return;
      }
      // 先查 u.jic.io FACE Registry(服务端匹配);离线或没命中再回退本机缓存
      const server = await resolveFaceOnServer(descriptor);
      if (server?.match) {
        setMatch({
          handle: server.match.name,
          faceId: `@${server.match.name}`,
          address: server.match.address,
          verified: true,
          confidence: server.match.confidence,
          source: "registry",
          identities: server.match.identities,
        });
        setState("matched");
        setMessage(`Registry 命中 @${server.match.name} · 置信度 ${server.match.confidence}%`);
        stopScanner();
        return;
      }
      const local = resolveLocalFace(descriptor);
      if (local) {
        setMatch({
          handle: local.record.handle,
          faceId: local.record.faceId,
          address: local.record.controllerAddress,
          verified: Boolean(local.record.addressVerified),
          confidence: local.confidence,
          source: "local",
        });
        setState("matched");
        setMessage(`本机缓存命中 · ${local.record.faceId}`);
        stopScanner();
      } else if (server) {
        setState("unknown");
        setMessage(server.count ? "检测到人脸，但 FACE Registry 里没有匹配的 Face ID" : "检测到人脸，但 FACE Registry 还没有任何绑定");
      } else {
        setState("unknown");
        setMessage("检测到人脸，但 Registry 暂时连不上，也没有本机缓存命中");
      }
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "人脸识别失败");
      stopScanner();
    } finally {
      analyzingRef.current = false;
    }
  };

  const startCamera = async () => {
    stopScanner();
    setMatch(null);
    setState("loading");
    setMessage("正在加载本机人脸模型…");
    try {
      await loadFaceRecognitionModels();
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("当前浏览器不支持摄像头");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 960 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) throw new Error("相机画面初始化失败");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setState("scanning");
      setMessage("正在寻找 Face ID… 请保持画面稳定");
      timerRef.current = window.setInterval(() => {
        if (videoRef.current?.readyState && videoRef.current.readyState >= 2) {
          resolveInput(videoRef.current);
        }
      }, 720);
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "无法打开相机");
      stopScanner();
    }
  };

  const choosePhoto = () => fileRef.current?.click();

  const onPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopScanner();
    setMatch(null);
    setState("loading");
    setMessage("正在读取照片中的人脸…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        await resolveInput(image);
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setState("error");
      setMessage("照片读取失败，请换一张重试");
    };
    image.src = objectUrl;
  };

  return (
    <main className="face3-page face3-scan-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <header className="face3-header">
        <a className="face3-brand" href="/" aria-label="FACE JIC scanner home"><span className="face3-mark">F³</span><span>FACE · JIC</span></a>
        <div className="face3-header-actions"><span className="face3-mvp-pill">SCAN · SEND LOVE</span><a href="/trade">FACE 交易</a></div>
      </header>

      <section className="face3-scan-hero">
        <div className="face3-scan-copy">
          <p className="face3-eyebrow">CAMERA TO LOVE</p>
          <h1>扫到一个人，<br /><em>传递一份 LOVE。</em></h1>
          <p>相机可以扫描真人、屏幕或照片，找到本人活体认领的 0x 收款地址；转账仍由你的钱包逐笔签名。</p>
          <div className="face3-scan-actions">
            {!loggedIn && <button type="button" className="cta" onClick={() => loginJic({ clearLocal: false })}>登录 JIC · 绑定我的脸</button>}
            {loggedIn && myName && myBound === false && (
              <button type="button" className="cta" onClick={() => { window.location.href = `/${myName}`; }}>◉ 前置摄像头绑定 @{myName}</button>
            )}
            <button type="button" className={loggedIn && myBound !== false ? "" : "secondary"} onClick={startCamera}>⌁ 打开相机扫描</button>
            <button type="button" className="secondary" onClick={choosePhoto}>从照片识别</button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} hidden />
          </div>
          <div className="face3-local-registry">
            <i />FACE Registry：{registryCount === null ? "…" : registryCount} 个 Face ID
            {loggedIn && myName && myBound && <> · <a href={`/${myName}`} style={{ color: "inherit" }}>✓ @{myName} 已绑定</a></>}
            {loggedIn && myName && myBound === false && <> · <a href={`/${myName}`} style={{ color: "inherit" }}>@{myName} 还没绑脸 →</a></>}
            {loggedIn && !myName && <> · <a href="https://u.jic.io/u" style={{ color: "inherit" }}>先去拿一个 JIC 短名字 →</a></>}
            {!loggedIn && <> · <a href="/me" style={{ color: "inherit" }}>绑定我的脸 →</a></>}
          </div>
        </div>

        <div className={`face3-scanner ${state === "scanning" ? "is-scanning" : ""}`}>
          <video ref={videoRef} playsInline muted />
          <div className="face3-scan-grid" />
          <div className="face3-scan-corners"><i /><i /><i /><i /></div>
          {state !== "scanning" && !match && <div className="face3-scan-placeholder"><span>◎</span><strong>FACE ID SCANNER</strong><small>照片可以识别，认领必须活体</small></div>}
          {state === "scanning" && <div className="face3-scan-line" />}
          <div className={`face3-scan-result state-${state}`}>
            <i />
            <div><strong>{match ? `@${match.handle}` : state === "scanning" ? "SCANNING" : state === "loading" ? "LOADING" : state === "unknown" ? "UNKNOWN FACE" : state === "error" ? "CHECK FAILED" : "READY"}</strong><small>{message}</small></div>
          </div>
          {match?.identities && match.identities.length > 0 && (
            <div className="face3-identity-chips" aria-label="已验证身份">
              {match.identities.map((identity) => (
                <span key={`${identity.type}:${identity.value}`} className="face3-identity-chip">
                  <b>{IDENTITY_LABEL[identity.type] || identity.type.toUpperCase()}</b>
                  {identityDisplay(identity)}
                  <i>✓</i>
                </span>
              ))}
            </div>
          )}
          {match && <a className="face3-open-id" href={`/${match.handle}`}>查看 {match.faceId} 的认领凭证 →</a>}
        </div>
      </section>

      {match && (
        <LoveTransferPanel
          faceId={match.faceId}
          handle={match.handle}
          recipient={match.address}
          verified={match.verified}
        />
      )}

      <section className="face3-scan-explainer">
        <div><span>SCAN</span><strong>照片也可以</strong><small>查询 Face ID，不取得控制权</small></div>
        <div><span>LIVE</span><strong>认领必须真人</strong><small>随机眨眼和转头挑战</small></div>
        <div><span>0x</span><strong>资产仍由钱包控制</strong><small>脸永远不是付款私钥</small></div>
      </section>
      <JicFloatingWidget />
    </main>
  );
}
